import { memo, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { CS, CS_m } from 'back-end-api'
import subDays from 'date-fns/subDays'
import isSameDay from 'date-fns/isSameDay'
import { useTranslation } from 'services/Translation'
import Typography from 'components/common/Typography'
import Separator from 'components/common/Separator'
import DefaultBtn from 'components/common/buttons/Default/Component'
import CalendarCard from 'components/patient/Content/Tabs/Diagnosis/Actions/AppointmentSchedule/Select/CalendarCard'
import { getDayStart, getDayEnd } from 'components/common/Month/Tools'
import { useAppointmentsDispatch, create } from 'services/Appointments'
import { useSummariesDispatch } from 'services/Summaries'
import { getAccessToken } from 'services/Authentication'
import { textPrimary, error, primary } from 'styles/Colors'
import { hp } from 'styles/Sizes'
import styled from 'styled-components'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 420px;
  width: 90%;
  max-height: min(90vh, 90dvh);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const CalendarWrapper = styled.div`
  transform: scale(0.85);
  transform-origin: top center;
  min-height: 300px;
  position: relative;
  z-index: 1;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
  margin-top: 64px;
  position: relative;
  z-index: 10;
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    title: getText('Reschedule Appointment', 'Patient_Details_Reschedule'),
    selectWindow: getText('Select a new appointment window', 'Patient_Details_Reschedule'),
    dateSelectionHelp: getText('Select one date for a specific day, or two dates to define a time window', 'Patient_Details_Reschedule'),
    selectedDateWindow: getText('Selected Date Window:', 'Patient_Details_Reschedule'),
    noDatesSelected: getText('No dates selected', 'Patient_Details_Reschedule'),
    currentWindow: getText('Current window', 'Patient_Details_Reschedule'),
    confirmBtn: getText('Send request', 'Patient_Details_Reschedule'),
    cancelBtn: getText('Cancel', 'Patient_Details_Reschedule'),
    rescheduling: getText('Sending...', 'Patient_Details_Reschedule'),
    errorMsg: getText('Failed to reschedule appointment', 'Patient_Details_Reschedule'),
  }[key])

interface TimeWindow {
  start: Date | null
  end: Date | null
}

interface RescheduleModalProps {
  visible: boolean
  onClose: () => void
  appointment: CS_m.ModelsAppointment
  onSuccess?: () => void
}

const RescheduleModal = ({ visible, onClose, appointment, onSuccess }: RescheduleModalProps) => {
  const { t, formatDate } = useTranslation(texts)
  const token = getAccessToken()
  const appointmentsDispatch = useAppointmentsDispatch()
  const summariesDispatch = useSummariesDispatch()

  const [validTimeWindow, setValidTimeWindow] = useState<TimeWindow>({ start: null, end: null })
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Get the current datetime window from the appointment if it exists
  const currentWindow = useMemo(() => {
    if (!appointment?.valid_datetime_window) return null
    return {
      begin: appointment.valid_datetime_window.begin ? new Date(appointment.valid_datetime_window.begin) : null,
      end: appointment.valid_datetime_window.end ? new Date(appointment.valid_datetime_window.end) : null,
    }
  }, [appointment])

  // Date selection logic matching the original CalendarCard behavior
  const handleDateSelection = useCallback((selectedDate: Date) => {
    if (!validTimeWindow.start && !validTimeWindow.end) {
      // First date selection
      setValidTimeWindow({
        start: getDayStart(selectedDate),
        end: getDayEnd(selectedDate),
      })
    } else if (validTimeWindow.start && validTimeWindow.end && isSameDay(validTimeWindow.start, validTimeWindow.end)) {
      // Currently have single date, selecting second date for range
      const firstDate = validTimeWindow.start
      if (isSameDay(firstDate, selectedDate)) {
        // Clicking same date - deselect
        setValidTimeWindow({ start: null, end: null })
      } else {
        // Create range
        const startDate = selectedDate < firstDate ? selectedDate : firstDate
        const endDate = selectedDate > firstDate ? selectedDate : firstDate
        setValidTimeWindow({
          start: getDayStart(startDate),
          end: getDayEnd(endDate),
        })
      }
    } else if (validTimeWindow.start && validTimeWindow.end) {
      // Currently have range, selecting new single date
      if (
        (validTimeWindow.start && isSameDay(validTimeWindow.start, selectedDate)) ||
        (validTimeWindow.end && isSameDay(validTimeWindow.end, selectedDate))
      ) {
        // Clicking on one of the range dates - deselect all
        setValidTimeWindow({ start: null, end: null })
      } else {
        // Select new single date
        setValidTimeWindow({
          start: getDayStart(selectedDate),
          end: getDayEnd(selectedDate),
        })
      }
    }
    setErrorMessage(null)
  }, [validTimeWindow])

  const getSelectedDates = useCallback(() => {
    const dates: Date[] = []
    if (validTimeWindow.start) dates.push(validTimeWindow.start)
    if (validTimeWindow.end && !isSameDay(validTimeWindow.start || new Date(), validTimeWindow.end)) {
      dates.push(validTimeWindow.end)
    }
    return dates
  }, [validTimeWindow])

  const getDateWindowText = useCallback(() => {
    if (!validTimeWindow.start && !validTimeWindow.end) {
      return t('noDatesSelected')
    }
    
    if (validTimeWindow.start && validTimeWindow.end) {
      if (isSameDay(validTimeWindow.start, validTimeWindow.end)) {
        // Single date selected
        return formatDate(validTimeWindow.start, 'dd MMMM, yyyy')
      }
      // Date range selected
      return `${formatDate(validTimeWindow.start, 'dd MMMM, yyyy')} - ${formatDate(validTimeWindow.end, 'dd MMMM, yyyy')}`
    }
    
    return t('noDatesSelected')
  }, [validTimeWindow, formatDate, t])

  const handleConfirm = useCallback(async () => {
    if (!validTimeWindow.start || !appointment?.id || !appointment?.summary_id) return

    setIsLoading(true)
    setErrorMessage(null)

    try {
      // Create the new appointment first so the status immediately shows "Appointment Pending"
      const durationNs = appointment.duration_ns || 15 * 60000000000 // Default 15 min in nanoseconds
      const durationMs = durationNs / 1000000 // Convert to milliseconds

      const createData = {
        summaryId: appointment.summary_id,
        validDateTimeWindow: validTimeWindow,
        duration: durationMs,
        doctorNote: appointment.doctor_note || '',
      }

      const newAppointment = await create({ token }, createData, { appointmentsDispatch, summariesDispatch })

      if (!newAppointment) {
        setErrorMessage(t('errorMsg'))
        return
      }

      // Then cancel the old appointment
      await CS.updateAppointment({
        id: appointment.id,
        status: CS_m.EndpointsUpdateAppointmentRequestStatus.declined,
      })

      onSuccess?.()
      onClose()
    } catch (err) {
      setErrorMessage(t('errorMsg'))
    } finally {
      setIsLoading(false)
    }
  }, [validTimeWindow, appointment, token, appointmentsDispatch, summariesDispatch, t, onSuccess, onClose])

  const handleCancel = useCallback(() => {
    setValidTimeWindow({ start: null, end: null })
    setErrorMessage(null)
    onClose()
  }, [onClose])

  if (!visible) return null

  return createPortal(
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <Typography
          type="title"
          className="font-csc65"
          text={t('title')}
          color={textPrimary}
          size={20}
          style={undefined}
        />
        
        <Separator size={hp(2)} />
        
        <Typography
          className="font-csc45"
          text={t('selectWindow')}
          color={textPrimary}
          size={14}
          style={undefined}
        />
        
        <Separator size={hp(1)} />
        
        <Typography
          className="font-csc45 text-center"
          text={t('dateSelectionHelp')}
          color={textPrimary}
          size={12}
          style={undefined}
        />
        
        <Separator size={hp(2)} />
        
        {currentWindow && (
          <>
            <Typography
              className="font-csc45"
              text={`${t('currentWindow')}: ${formatDate(currentWindow.begin!, 'dd MMM')} - ${formatDate(currentWindow.end!, 'dd MMM yyyy')}`}
              color={primary}
              size={12}
              style={{ textAlign: 'center' }}
            />
            <Separator size={hp(2)} />
          </>
        )}
        
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Typography
            className="font-csc65"
            text={t('selectedDateWindow')}
            color={textPrimary}
            size={13}
            style={{ whiteSpace: 'nowrap' }}
          />
          <Typography
            className="font-csc45"
            text={getDateWindowText()}
            color="#EB7C0C"
            size={13}
            style={{ fontWeight: 'bold', textAlign: 'center' }}
          />
        </div>
        
        <Separator size={hp(2)} />
        
        <CalendarWrapper>
          <CalendarCard
            field={{
              value: null,
              onChange: handleDateSelection,
            }}
            events={[]}
            appointmentDuration={15}
            disableBefore={subDays(new Date(), 1)}
            selectedDates={getSelectedDates()}
            isRangeSelection
          />
        </CalendarWrapper>
        
        {errorMessage && (
          <>
            <Separator size={hp(1)} />
            <Typography
              className="font-csc45"
              text={errorMessage}
              color={error}
              size={14}
              style={{ textAlign: 'center' }}
            />
          </>
        )}
        
        <ButtonsContainer>
          <DefaultBtn
            label={t('cancelBtn')}
            onClick={handleCancel}
            className="font-csc65"
            type="button"
            style={{ width: '7rem' }}
            color={textPrimary}
            disabled={isLoading}
            nextIcon={undefined}
          />
          <DefaultBtn
            label={isLoading ? t('rescheduling') : t('confirmBtn')}
            onClick={handleConfirm}
            className="font-csc65"
            type="button"
            reversedColors
            style={{ width: '8rem' }}
            color={textPrimary}
            disabled={!validTimeWindow.start || isLoading}
            nextIcon={undefined}
          />
        </ButtonsContainer>
      </ModalContent>
    </ModalOverlay>,
    document.body
  )
}

RescheduleModal.whyDidYouRender = true

RescheduleModal.defaultProps = {
  onSuccess: undefined,
}

export default memo(RescheduleModal)
